import React, { useRef, useMemo } from 'react';
import Plyr from 'plyr-react';
import { ButtonLink } from './shared/ButtonLink';
import 'plyr-react/plyr.css';
import type { VideoComponent as VideoComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { EMBED_ASPECT_RATIO_CSS, getEmbedSrc, isEmbedProvider } from '../utils/videoEmbed';

interface VideoComponentProps {
  component: VideoComponentType;
}

export const VideoComponent: React.FC<VideoComponentProps> = ({ component }) => {
  const plyrRef = useRef<any>(null);
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const { data } = component;
  const safeData = component.type === 'video' ? data : null;

  const sourceType = safeData?.source?.sourceType || 'upload';
  const isEmbed = isEmbedProvider(sourceType);

  const {
    title,
    subtitle,
    description,
    source,
    thumbnail,
    playback,
    aspectRatio: _aspectRatio,
    width = 'container',
    maxWidth,
    buttons = [],
    buttonsPosition = 'below',
    backgroundColor,
    textColor,
    padding,
    borderRadius = 'medium',
    border,
    shadow,
    titleColor,
    titleSize = 'large',
    subtitleColor,
    showMetadata = false,
    showTimestamp = false,
    responsiveHeight = true,
    mobileOptimized = true,
    animateOnScroll = false,
    animationDelay = 0,
  } = safeData || ({} as any);

  const spacing = (safeData as any)?.spacing || 'normal';

  // Get spacing classes based on design system like other components
  const getSpacingClasses = () => {
    // Use same spacing system as MediaTextComponent and other components
    const spacingMap = {
      compact: 'py-8 md:py-12',
      normal: 'py-12 md:py-16',
      spacious: 'py-16 md:py-24',
    };

    // Use spacing from data if available, otherwise fall back to custom padding
    if (spacing && spacingMap[spacing as keyof typeof spacingMap]) {
      return spacingMap[spacing as keyof typeof spacingMap];
    }

    // If custom padding is specified, use it
    if (padding) {
      const { top, bottom } = padding;
      return `py-[${top}px] md:py-[${bottom}px]`;
    }

    // Default to normal spacing
    return spacingMap.normal;
  };

  // Get title size classes - consistent with other components
  const getTitleSizeClasses = () => {
    switch (titleSize) {
      case 'small':
        return 'text-2xl md:text-3xl';
      case 'medium':
        return 'text-3xl md:text-4xl';
      case 'large':
        return 'text-4xl md:text-5xl';
      case 'xl':
        return 'text-5xl md:text-6xl';
      default:
        return 'text-4xl md:text-5xl';
    }
  };

  const getWidthClasses = () => {
    switch (width) {
      case 'full':
        return 'w-full';
      case 'container':
        return 'w-full max-w-4xl mx-auto';
      case 'fixed':
        return maxWidth ? `max-w-[${maxWidth}px] mx-auto` : 'max-w-4xl mx-auto';
      default:
        return 'w-full max-w-4xl mx-auto';
    }
  };

  // Get border radius classes
  const getBorderRadiusClasses = () => {
    switch (borderRadius) {
      case 'none':
        return 'rounded-none';
      case 'small':
        return 'rounded-sm';
      case 'medium':
        return 'rounded-lg';
      case 'large':
        return 'rounded-xl';
      default:
        return 'rounded-lg';
    }
  };

  // Get shadow classes
  const getShadowClasses = () => {
    if (!shadow?.enabled) return '';
    switch (shadow.size) {
      case 'small':
        return 'shadow-md';
      case 'medium':
        return 'shadow-lg';
      case 'large':
        return 'shadow-xl';
      default:
        return 'shadow-lg';
    }
  };

  // Color priority: component.customColors > deprecated fields > theme colors
  const bgColor =
    (data as any).customColors?.background ||
    backgroundColor ||
    themeStyles.video?.background ||
    themeStyles.mediaText?.background;
  const headingColor =
    (data as any).customColors?.title ||
    titleColor ||
    themeStyles.video?.titleColor ||
    themeStyles.mediaText?.titleColor ||
    '#ffffff';
  const subtitleThemeColor =
    (data as any).customColors?.subtitle ||
    subtitleColor ||
    themeStyles.video?.subtitleColor ||
    themeStyles.mediaText?.titleColor ||
    '#ffffff';
  const mainTextColor =
    (data as any).customColors?.text ||
    textColor ||
    themeStyles.video?.textColor ||
    themeStyles.mediaText?.textColor ||
    '#ffffff';
  const buttonBgColor =
    (data as any).customColors?.buttonBackground ||
    themeStyles.video?.buttonBg ||
    themeStyles.mediaText?.accentColor ||
    '#DC2626';
  const buttonTextColor =
    (data as any).customColors?.buttonText || themeStyles.video?.buttonText || '#ffffff';

  // Get button style using theme colors
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: 'none',
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Get button classes without color-related styles
  const getButtonClasses = () => {
    const baseClasses =
      'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 hover:opacity-90';
    return baseClasses;
  };

  // Memoize Plyr source/options for uploaded videos (embeds use direct iframes)
  const videoSrc = useMemo(() => {
    if (!source?.url || isEmbed) return { type: 'video' as const, sources: [] };
    return {
      type: 'video' as const,
      sources: [
        { src: source.cloudfrontUrl || source.url, type: source.mimeType || 'video/mp4' },
        ...(source.alternatives?.webm
          ? [{ src: source.alternatives.webm, type: 'video/webm' }]
          : []),
        ...(source.alternatives?.ogg ? [{ src: source.alternatives.ogg, type: 'video/ogg' }] : []),
      ],
      poster: thumbnail?.cloudfrontUrl || thumbnail?.url,
      tracks:
        source.captions?.map((caption: any) => ({
          kind: 'captions' as const,
          label: caption.label,
          srcLang: caption.language,
          src: caption.url,
          default: caption.default || false,
        })) || [],
    };
  }, [
    isEmbed,
    source?.url,
    source?.cloudfrontUrl,
    source?.mimeType,
    source?.alternatives?.webm,
    source?.alternatives?.ogg,
    source?.captions,
    thumbnail?.cloudfrontUrl,
    thumbnail?.url,
  ]);

  const plyrOptions = useMemo(() => {
    return {
      controls:
        playback?.controls !== false
          ? [
              'play-large',
              'play',
              'progress',
              'current-time',
              'mute',
              'volume',
              ...(playback?.allowFullscreen !== false ? ['fullscreen'] : []),
              ...(playback?.showCaptions && source?.captions?.length ? ['captions'] : []),
              ...(playback?.allowDownload ? ['download'] : []),
            ]
          : [],
      autoplay: playback?.autoplay || false,
      muted: playback?.muted || false,
      loop: { active: playback?.loop || false },
      settings: ['captions', 'quality', 'speed'],
      volume: playback?.defaultVolume || 0.8,
      hideControls: !playback?.controls,
      clickToPlay: true,
      keyboard: { focused: true, global: false },
      tooltips: { controls: true, seek: true },
      storage: { enabled: false },
      quality: { default: playback?.quality === 'auto' ? 576 : 720, options: [576, 720, 1080] },
      previewThumbnails: { enabled: false },
      captions: {
        active: playback?.showCaptions || false,
        language: playback?.captionsLanguage || 'auto',
      },
    };
  }, [
    playback?.controls,
    playback?.allowFullscreen,
    playback?.showCaptions,
    playback?.allowDownload,
    playback?.autoplay,
    playback?.muted,
    playback?.loop,
    playback?.defaultVolume,
    playback?.quality,
    playback?.captionsLanguage,
    source?.captions?.length,
  ]);

  if (component.type !== 'video') {
    return (
      <div className="w-full p-8 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Invalid component type for video component</p>
      </div>
    );
  }

  const hasVideoSource = !!safeData?.source?.url;

  // Component styles for the video player
  const playerStyle = {
    ...(border?.enabled && {
      border: `${border.width || 2}px ${border.style || 'solid'} ${border.color || '#e5e7eb'}`,
    }),
    ...(shadow?.color &&
      shadow.enabled && {
        boxShadow: `0 4px 6px -1px ${shadow.color}`,
      }),
  };

  return (
    <section
      className={`w-full ${getSpacingClasses()} relative`}
      style={{
        backgroundColor: bgColor,
        ...(animateOnScroll &&
          animationDelay > 0 && {
            animationDelay: `${animationDelay}ms`,
          }),
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Content */}
        {(title || subtitle || description) && (
          <div className="mb-12 flex flex-col items-center">
            {/* Subtitle */}
            {subtitle && (
              <p
                className="text-lg md:text-xl font-medium mb-4 opacity-80 w-full text-center"
                style={{ color: subtitleThemeColor, ...bodyFontStyle }}
              >
                {subtitle}
              </p>
            )}

            {/* Title */}
            {title && (
              <h2
                className={`${getTitleSizeClasses()} font-bold mb-6 leading-tight w-full text-center`}
                style={{ color: headingColor, ...headingFontStyle }}
              >
                {title}
              </h2>
            )}

            {/* Description */}
            {description && (
              <>
                <style>{`
                  .video-description-${component.id}, .video-description-${component.id} * {
                    color: ${mainTextColor} !important;
                  }
                `}</style>
                <div
                  className={`video-description-${component.id} text-lg leading-relaxed max-w-3xl mb-8 text-center mx-auto`}
                  style={{ ...bodyFontStyle }}
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </>
            )}
          </div>
        )}

        {/* Video Player Container */}
        <div className={`${getWidthClasses()}`}>
          <div
            key={isEmbed ? 'embed' : hasVideoSource ? 'player' : 'empty'}
            className={`
              relative ${getBorderRadiusClasses()} ${getShadowClasses()}
              overflow-hidden bg-black
              ${mobileOptimized ? 'w-full' : ''}
              ${responsiveHeight ? 'h-auto' : 'h-64 md:h-96'}
            `}
            style={playerStyle}
          >
            {isEmbed && source?.url ? (
              <div style={{ aspectRatio: EMBED_ASPECT_RATIO_CSS }}>
                <iframe
                  src={isEmbedProvider(sourceType) ? getEmbedSrc(sourceType, source.url) || '' : ''}
                  className="w-full h-full"
                  style={{ border: 0, width: '100%', height: '100%' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={title || 'Video'}
                />
              </div>
            ) : hasVideoSource && !isEmbed ? (
              <Plyr
                source={videoSrc}
                options={plyrOptions}
                ref={plyrRef}
                data-testid="plyr-video"
              />
            ) : (
              <div
                className="flex items-center justify-center text-gray-400"
                style={{ aspectRatio: EMBED_ASPECT_RATIO_CSS }}
              >
                <p className="text-lg">
                  {isEmbed ? 'Enter a video URL to preview' : 'Upload a video to get started'}
                </p>
              </div>
            )}

            {/* Overlay Buttons */}
            {buttonsPosition === 'overlay' && buttons.length > 0 && (
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-3 justify-center">
                {buttons
                  .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                  .map((button: any, index: number) => (
                    <ButtonLink
                      key={button.id || index}
                      url={button.url}
                      target={button.target || '_self'}
                      className={`${getButtonClasses()} ${
                        button.size === 'small'
                          ? 'px-4 py-2 text-sm'
                          : button.size === 'large'
                            ? 'px-8 py-4 text-lg'
                            : 'px-6 py-3 text-base'
                      }`}
                      style={getButtonStyles(button.style)}
                    >
                      {button.text}
                    </ButtonLink>
                  ))}
              </div>
            )}
          </div>

          {/* Video Metadata */}
          {showMetadata && (
            <div className="mt-6 text-sm text-gray-600 flex flex-wrap gap-4 justify-center">
              {source.duration && showTimestamp && (
                <span className="flex items-center gap-1">
                  <span className="font-medium">Duration:</span>
                  {Math.floor(source.duration / 60)}:
                  {(source.duration % 60).toString().padStart(2, '0')}
                </span>
              )}
              {source.width && source.height && (
                <span className="flex items-center gap-1">
                  <span className="font-medium">Resolution:</span>
                  {source.width}×{source.height}
                </span>
              )}
              {source.fileSize && (
                <span className="flex items-center gap-1">
                  <span className="font-medium">Size:</span>
                  {(source.fileSize / (1024 * 1024)).toFixed(1)}MB
                </span>
              )}
            </div>
          )}
          {/* Buttons Below/Beside Video */}
          {(buttonsPosition === 'below' || buttonsPosition === 'beside') && buttons.length > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
              {buttons
                .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                .map((button: any, index: number) => (
                  <ButtonLink
                    key={button.id || index}
                    url={button.url}
                    target={button.target || '_self'}
                    className={`${getButtonClasses()} ${
                      button.size === 'small'
                        ? 'px-4 py-2 text-sm'
                        : button.size === 'large'
                          ? 'px-8 py-4 text-lg'
                          : 'px-6 py-3 text-base'
                    } transition-all duration-200 transform hover:scale-105`}
                    style={getButtonStyles(button.style)}
                  >
                    {button.text}
                  </ButtonLink>
                ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
